import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Users } from 'lucide-react';
import { getText } from '../data/content';
import { theme, combineClasses } from '../theme/theme';

/**
 * ============================================================================
 * 组件模块：components/TeamCarousel.jsx
 * 
 * 说明：
 * - 团队成员轮播组件，随机显示并可滚动切换
 * - 在主页展示团队成员预览
 * - 支持左右滚动和自动轮播
 * 
 * Props:
 * - lang: string - 当前语言
 * - onMemberClick: function - 点击成员的回调函数
 * ============================================================================
 */
const TeamCarousel = ({ lang, themeMode = "dark", onMemberClick, includePI }) => {
  const isLight = themeMode === "light";
  // 获取所有团队成员
  const allMembers = getText('team.members', lang);
  // 获取PI信息
  const piDetails = includePI ? getText('team.piDetails', lang) : null;

  const piAsMember = includePI
    ? {
        name: getText('team.piTitle', lang),
        role: getText('team.piRole', lang),
        focus: piDetails?.focus || "",
        // 轮播只用到 details.image（你卡片头像用这个）
        details: {
          image: piDetails?.image || "",
        },
        __isPI: true,
      }
    : null;

  // 合并团队成员和PI（如果includePI），PI排在前列
  const carouselList = [
    ...(piAsMember ? [piAsMember] : []),
    ...allMembers,
  ];

  const [shuffledMembers, setShuffledMembers] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // 每次成员/PI变化时随机排序
  useEffect(() => {
    // 洗牌算法，保证每次都新随机（除长度不变外触发）
    const shuffled = [...carouselList].sort(() => Math.random() - 0.5);
    setShuffledMembers(shuffled);
  }, [carouselList.length]);

  // 显示4个成员
  const displayedMembers = shuffledMembers.slice(currentIndex, currentIndex + 4);

  // 自动轮播
  useEffect(() => {
    if (!isAutoPlaying || shuffledMembers.length === 0) return;

    // 如果成员（含PI）数量少于等于4，不需要轮播
    if (shuffledMembers.length <= 4) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const nextIndex = prev + 4;
        // 如果超出范围，回到开头
        if (nextIndex >= shuffledMembers.length) {
          return 0;
        }
        return nextIndex;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, shuffledMembers.length]);

  const goToNext = () => {
    setCurrentIndex((prev) => {
      const nextIndex = prev + 4;
      return nextIndex >= shuffledMembers.length ? 0 : nextIndex;
    });
  };

  const goToPrev = () => {
    setCurrentIndex((prev) => {
      const prevIndex = prev - 4;
      return prevIndex < 0 ? Math.max(0, shuffledMembers.length - 4) : prevIndex;
    });
  };

  if (shuffledMembers.length === 0) return null;

  return (
    <div 
      className="relative group"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
    >
      {/* 成员卡片网格 */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-6 relative overflow-hidden">
        {displayedMembers.map((member, index) => (
          <div
            key={`${currentIndex}-${index}`}
            className={combineClasses(
              isLight ? theme.cardsLight.base : theme.cards.base,
              "p-6 text-center cursor-pointer",
              "hover:border-cyan-500/50 transition-all duration-300",
              "hover:-translate-y-2 animate-slide-in"
            )}
            onClick={() => onMemberClick && onMemberClick(member)}
          >
            <div className={combineClasses(
              "w-40 h-40 mx-auto mb-4 rounded-full flex items-center justify-center overflow-hidden border-2", // 这里修改卡片头像尺寸
              isLight 
                ? "bg-gray-100 border-gray-200" 
                : "bg-white/5 border-white/10"
            )}>
              {member.details?.image ? (
                <img 
                  src={member.details.image} 
                  alt={member.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={combineClasses(
                "w-full h-full flex items-center justify-center",
                member.details?.image ? "hidden" : ""
              )}>
                <Users className={combineClasses(
                  "w-10 h-10",
                  isLight ? "text-gray-500" : "text-gray-400"
                )} />
              </div>
            </div>
            <h4 className={combineClasses(
              "text-lg font-bold mb-1",
              isLight ? "text-gray-900" : "text-white"
            )}>{member.name}</h4>
            <p className="text-sm text-cyan-400 mb-2">{member.role}</p>
            <p className={combineClasses(
              "text-xs",
              isLight ? "text-gray-600" : "text-gray-500"
            )}>{member.focus}</p>
          </div>
        ))}
      </div>

      {/* 导航按钮 - 只有超过4个成员才显示 */}
      {shuffledMembers.length > 4 ? (
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={goToPrev}
            className={combineClasses(
              "p-2 rounded-full transition-all opacity-0 group-hover:opacity-100",
              isLight 
                ? "bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-900" 
                : "bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white"
            )}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          {/* 进度指示器 */}
          <div className="flex gap-2">
            {Array.from({ length: Math.ceil(shuffledMembers.length / 4) }).map((_, index) => {
              const pageStart = index * 4;
              const isActive = currentIndex >= pageStart && currentIndex < pageStart + 4;
              return (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(pageStart)}
                  className={combineClasses(
                    "w-2 h-2 rounded-full transition-all duration-300",
                    isActive 
                      ? "bg-cyan-400 w-6" 
                      : isLight 
                        ? "bg-gray-400/50 hover:bg-gray-600/50" 
                        : "bg-white/30 hover:bg-white/50"
                  )}
                />
              );
            })}
          </div>
          
          <button
            onClick={goToNext}
            className={combineClasses(
              "p-2 rounded-full transition-all opacity-0 group-hover:opacity-100",
              isLight 
                ? "bg-gray-200 hover:bg-gray-300 text-gray-600 hover:text-gray-900" 
                : "bg-white/10 hover:bg-white/20 text-gray-400 hover:text-white"
            )}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      ) : null}

      <style jsx>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default TeamCarousel;
